import { defaultRosterLockAt, rosterLockState } from '@league/shared/roster-lock';
import type { MarketListResponse, MetaResponse, PayoutStatusResponse, VenueListResponse } from '@league/shared/contract';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { LINK_CLASS } from '@league/ui';

import { Button, ErrorNote, Field, Input } from '../../../components/ui';
import { ApiError, apiFetch, apiPatch, apiPut } from '../../../lib/api';
import { LEAGUE_SESSIONS } from '@league/shared/hq';
import { WEEKDAYS, type LeagueContext } from '../LeagueWorkspace';
import { ROSTER_LOCK_IN_V1 } from '@league/shared/features';
import { EMAIL_SWITCHES } from '@league/shared/emails';
import { atLocalTimeOn, dateInputIn, timeInputIn, weekdayIn } from '@league/shared/time';

import { zoneName } from '../../../lib/when';
import { NO_VENUE, type VenueChoice, VenueChooser, venueChoiceComplete, venuePayload } from '../VenueChooser';

/**
 * THE TENTH TAB, which the mockups do not draw and the build spec insists on.
 *
 * It holds the things that need changing after a league exists and have nowhere else to live: the
 * name, the sessions, the venue, the cap, the roster-lock cutoff. The roster lock is
 * the clearest proof it was needed — the Roster tab's banner announces a date that until now nothing
 * could change.
 *
 * Two things are deliberately absent:
 *
 *   SCORING CONFIG — not finalised. The defaults are provisional and the field is per-league
 *   precisely so settling it later does not rewrite leagues already played. An editor for a shape
 *   nobody has agreed would invite a director to configure something about to change underneath her.
 *
 *   DELETE — there is no such thing. Archiving hides a league and keeps every score, and a league
 *   is somebody's three weeks.
 */

/** `<input type="date">` wants `YYYY-MM-DD` in local time, which `toISOString` does not give. */
function toDateInput(date: Date): string {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

export function SettingsTab() {
  const { league, sessions, refresh } = useOutletContext<LeagueContext>();
  const queryClient = useQueryClient();

  const [name, setName] = useState(league.name);
  const [playerCap, setPlayerCap] = useState(String(league.playerCap));
  const [settingUpPayments, setSettingUpPayments] = useState(false);
  const [price, setPrice] = useState(league.seatPriceCents ? (league.seatPriceCents / 100).toFixed(2).replace(/\.00$/, '') : '');
  const meta = useQuery({ queryKey: ['meta'], queryFn: () => apiFetch<MetaResponse>('/api/meta') });
  const paymentsOpen = meta.data?.playerPaymentsOpen === true;
  const payouts = useQuery({
    queryKey: ['payouts'],
    queryFn: () => apiFetch<PayoutStatusResponse>('/api/billing/payouts'),
    enabled: paymentsOpen,
    refetchInterval: (query) => (query.state.data?.payouts === 'checking' ? 5_000 : false),
  });
  const payoutsReady = payouts.data?.payouts === 'ready';
  const priceCents = price.trim() === '' ? null : Math.round(Number(price) * 100);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['league', league.id] });
    void queryClient.invalidateQueries({ queryKey: ['roster', league.id] });
    refresh();
  };

  const save = useMutation({
    mutationFn: (patch: Record<string, unknown>) => apiPatch(`/api/leagues/${league.id}`, patch),
    onSuccess: invalidate,
  });

  /*
    A SESSION'S DATE, START AND STOP, BUILT IN THE HOST CITY'S ZONE (owner, 2026-10-12). This used
    to be `new Date(`${date}T19:00:00`)` — 7pm on the director's own computer, so a laptop left on
    another zone moved the session by hours.
  */
  const moveSession = useMutation({
    mutationFn: ({ id, startsAt, endsAt }: { id: string; startsAt: string; endsAt: string | null }) =>
      apiPatch(`/api/sessions/${id}`, { startsAt, endsAt }),
    onSuccess: invalidate,
  });

  /* How many sessions — any number from one to twenty-four, until session one starts. */
  const [countText, setCountText] = useState(String(sessions.length));
  const countValue = Number(countText);
  const countChanged =
    Number.isInteger(countValue) && countValue >= LEAGUE_SESSIONS.min && countValue <= LEAGUE_SESSIONS.max && countValue !== sessions.length;
  const setCount = useMutation({
    mutationFn: (count: number) => apiPut(`/api/leagues/${league.id}/session-count`, { count }),
    onSuccess: invalidate,
  });

  const first = sessions.find((s) => s.sessionNumber === 1);
  const derived = first ? defaultRosterLockAt(new Date(first.startsAt)) : null;

  const lockAt = league.rosterLockAt ? new Date(league.rosterLockAt) : null;
  const lock = rosterLockState(lockAt, new Date());

  /* Said beside the price, because the top of this page is out of sight from there. */
  const paymentSave = save.variables !== undefined && ('seatPriceCents' in save.variables || 'playersPayOnline' in save.variables);
  const priceSaved = save.isSuccess && paymentSave && priceCents === league.seatPriceCents;
  const error = (paymentSave ? null : save.error) ?? moveSession.error ?? setCount.error;

  return (
    <div className="flex max-w-xl flex-col gap-10">
      {error ? (
        <ErrorNote>
          {error instanceof ApiError ? error.message : 'Could not save that.'}
        </ErrorNote>
      ) : null}

      {league.status === 'completed' || league.status === 'archived' ? null : (
        <Section title="Registration">
          {league.status === 'registration' ? (
            <>
              <p className="text-[15px]">
                Registration is open
                {meta.data ? <> · <a className={LINK_CLASS} href={`${meta.data.siteOrigin}/leagues/${encodeURIComponent(league.slug)}`} target="_blank" rel="noreferrer">View on the site</a></> : null}
              </p>
              <div>
                <Button size="app" variant="secondary" disabled={save.isPending} onClick={() => save.mutate({ status: 'draft' })}>Close registration</Button>
              </div>
            </>
          ) : (
            <div>
              <Button size="app" disabled={save.isPending} onClick={() => save.mutate({ status: 'registration' })}>Open registration</Button>
            </div>
          )}
        </Section>
      )}

      <Section title="Scoring">
        <p className="text-sm text-ink-2">Choose before entering scores. Existing results keep their original rules.</p>
        <label htmlFor="scoring-mode">Scoring mode</label>
        <select id="scoring-mode" className="min-h-11 rounded border border-control-border bg-paper p-2" value={league.scoringConfig.mode ?? ''} disabled={save.isPending} onChange={event => save.mutate({ scoringMode: event.target.value })}>
          <option value="" disabled>Original league rules — choose a mode before play</option>
          <option value="social">Social scoring — no deductions</option>
          <option value="tournament">Competitive scoring — bonuses and deductions</option>
        </select>
      </Section>
      <Section title="The league">
        <Field label="Name" htmlFor="league-name">
          <Input id="league-name" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>

        <Field
          label="Player cap"
          htmlFor="player-cap"
          hint="A maximum, not a target. Plenty of leagues run with a dozen."
        >
          <Input
            id="player-cap"
            type="number"
            min={4}
            value={playerCap}
            onChange={(e) => setPlayerCap(e.target.value)}
          />
        </Field>

        <div>
          <Button
            size="app"
            disabled={save.isPending}
            onClick={() =>
              save.mutate({ name: name.trim(), playerCap: Number(playerCap) })
            }
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </Section>

      {/*
        PLAYERS PAY ONLINE — owner, 2026-10-06. Her choice, league by league; off, nothing changes.
        The one line under it is money she will not see, which the screen cannot show any other way.
      */}
      {paymentsOpen ? (
        <Section title="Payment">
          {!league.playersPayOnline && !settingUpPayments ? (
            <Button size="app" variant="secondary" onClick={() => setSettingUpPayments(true)}>Set up online payments</Button>
          ) : <>

          <Field label="Price per player" htmlFor="seat-price">
            <div className="relative max-w-40">
              <span aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[15px] text-ink-2">$</span>
              <Input
                id="seat-price"
                inputMode="decimal"
                placeholder="60"
                className="pl-7"
                value={price}
                onChange={(e) => setPrice(e.target.value.replace(/[^\d.]/g, ''))}
              />
            </div>
          </Field>
          <label className="flex min-h-11 items-center gap-3 text-[15px]">
            <input
              type="checkbox"
              className="size-5 accent-signal"
              checked={league.playersPayOnline}
              disabled={save.isPending || (!league.playersPayOnline && (!priceCents || !payoutsReady))}
              onChange={(e) => { const enabled = e.target.checked; save.mutate(enabled ? { playersPayOnline: true, seatPriceCents: priceCents } : { playersPayOnline: false }, { onSuccess: () => setSettingUpPayments(false) }); }}
            />
            Players pay online when they register
          </label>
          {league.playersPayOnline ? (
            <p className="text-[13px] text-ink-2">Stripe’s fee (2.9% + 30¢) comes out of each payment.</p>
          ) : payouts.data?.payouts === 'checking' ? (
            <p className="text-[13px] text-ink-2">Stripe is checking your details. This usually takes a minute or two.</p>
          ) : !payoutsReady && payouts.data ? (
            <p className="text-[13px] text-ink-2">
              <Link className={LINK_CLASS} to="/director/settings">Set up payments</Link> first.
            </p>
          ) : null}
          {paymentSave && save.error ? <ErrorNote>{save.error instanceof ApiError ? save.error.message : 'Could not save that.'}</ErrorNote> : null}
          <div className="flex flex-wrap items-center gap-3">
            <Button size="app" disabled={save.isPending || priceCents === league.seatPriceCents || (priceCents !== null && (!Number.isFinite(priceCents) || priceCents < 100))} onClick={() => save.mutate({ seatPriceCents: priceCents })}>
              Save price
            </Button>
            {priceSaved ? <span role="status" className="text-[14px] text-ink-2">Price saved</span> : null}
            {!league.playersPayOnline ? <Button size="app" variant="quiet" onClick={() => setSettingUpPayments(false)}>Cancel setup</Button> : null}
          </div>
          </>}
        </Section>
      ) : null}

      <VenueSettings
        key={league.venueId ?? 'none'}
        marketId={league.marketId}
        venueId={league.venueId}
        saving={save.isPending}
        onSave={(patch) => save.mutate(patch)}
      />

      <Section title="The sessions" note={zoneName(league.timeZone)}>
        {sessions.length === 0 ? (
          /*
            Every league gets its sessions when it is created, so this should not happen. It says so
            plainly rather than showing an empty box, because this is the screen somebody would come
            to in order to fix it — and "there is nothing here" is not the same as "this is broken",
            which is what an empty bordered list looks like.
          */
          <p className="rounded-md border border-dashed border-light px-5 py-8 text-center text-[14px] text-ink-3">
            No sessions are scheduled for this league.
          </p>
        ) : null}

        {/* How many, until session one starts (owner, 2026-10-19). Added sessions follow the last. */}
        {first && new Date(first.startsAt).getTime() > Date.now() ? (
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Number of sessions" htmlFor="session-count">
              <Input
                id="session-count"
                type="number"
                inputMode="numeric"
                min={LEAGUE_SESSIONS.min}
                max={LEAGUE_SESSIONS.max}
                value={countText}
                onChange={(e) => setCountText(e.target.value)}
                className="tabular w-28"
              />
            </Field>
            <Button
              size="app"
              disabled={setCount.isPending || !countChanged}
              onClick={() => setCount.mutate(Number(countText))}
            >
              {setCount.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        ) : null}

        <ol className="flex flex-col divide-y divide-light rounded-md border border-light bg-paper empty:hidden">
          {sessions.map((session) => (
            <SessionTimes
              key={session.id}
              session={session}
              zone={league.timeZone}
              onSave={(startsAt, endsAt) => moveSession.mutate({ id: session.id, startsAt, endsAt })}
            />
          ))}
        </ol>
      </Section>

      {/*
        EACH KIND OF EMAIL, ON OR OFF, for this league (Courtney, 2026-10-12). Saved as soon as it is
        ticked, like the payment switch above.
      */}
      <Section title="Emails">
        <div className="flex flex-col">
          {EMAIL_SWITCHES.map((kind) => {
            const on = !league.emailsOff.includes(kind.key);
            return (
              <label key={kind.key} className="flex min-h-11 items-center gap-3 text-[15px]">
                <input
                  type="checkbox"
                  className="size-5 accent-deep"
                  checked={on}
                  disabled={save.isPending}
                  onChange={(e) =>
                    save.mutate({
                      emailsOff: e.target.checked
                        ? league.emailsOff.filter((key) => key !== kind.key)
                        : [...league.emailsOff, kind.key],
                    })
                  }
                />
                {kind.label}
              </label>
            );
          })}
        </div>
      </Section>

      {/* The lock is not part of V1 — see `@league/shared/features`. */}
      {ROSTER_LOCK_IN_V1 ? (
        <Section title="Roster lock">
          <p className="text-[14px] leading-relaxed text-ink-2">
            HQ prints name tents and score sheets from the roster as it stands on this date. Changes
            afterwards are still possible — they flag the pack for HQ to re-check.
          </p>

          <Field
            label="Locks on"
            htmlFor="lock-date"
            hint={
              league.rosterLockManual
                ? 'Set by hand. It will stay where it is even if the first session moves.'
                : derived
                  ? `Following the first session — ten days before it. Set a date to pin it instead.`
                  : 'No first session yet, so there is nothing to derive it from.'
            }
          >
            {/*
              `key` on the stored value, because this is an uncontrolled input: React will not touch
              a `defaultValue` after mount, so reverting the lock to the derived date changed the hint
              underneath a box still showing the old one. Keying it remounts the box when the value it
              represents actually changes.
            */}
            <Input
              key={lockAt?.toISOString() ?? 'none'}
              id="lock-date"
              type="date"
              defaultValue={lockAt ? toDateInput(lockAt) : ''}
              onChange={(e) =>
                e.target.value &&
                save.mutate({ rosterLockAt: new Date(`${e.target.value}T00:00:00`).toISOString() })
              }
            />
          </Field>

          {/*
            No date in the label. Predicting it here meant computing the derived date in local time
            while the server computes it from the stored UTC instant, and the two disagreed by a day
            on a league whose sessions start in the evening — so the button promised the 15th and
            produced the 16th. The real fix is a per-league time zone, which the schema does not have
            yet (see DECISIONS.md); until then, do not make a prediction that can be wrong.
          */}
          {league.rosterLockManual && derived ? (
            <div>
              <Button
                variant="secondary"
                size="inline"
                onClick={() => save.mutate({ rosterLockAt: null })}
              >
                Go back to following the first session
              </Button>
            </div>
          ) : null}

          {lock.locked ? (
            <p className="text-[13px] text-ink-2">This roster is already locked.</p>
          ) : null}
        </Section>
      ) : null}
    </div>
  );
}

function Section({
  title,
  note,
  children,
}: {
  title: string;
  note?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-4">
      <div>
        <h2 className="font-display text-xl leading-none">{title}</h2>
        {note ? <p className="mt-1.5 text-[13px] text-ink-2">{note}</p> : null}
      </div>
      {children}
    </section>
  );
}

/**
 * ONE SESSION'S DATE, START AND STOP, read and written in the host city's zone. Saved when she leaves
 * a box, as the date alone used to be; a stop before the start is said on the row and not sent.
 */
function SessionTimes({
  session,
  zone,
  onSave,
}: {
  session: LeagueContext['sessions'][number];
  zone: string;
  onSave: (startsAt: string, endsAt: string | null) => void;
}) {
  const startsAt = new Date(session.startsAt);
  const saved = {
    date: dateInputIn(startsAt, zone),
    start: timeInputIn(startsAt, zone),
    end: session.endsAt ? timeInputIn(new Date(session.endsAt), zone) : '',
  };
  const [date, setDate] = useState(saved.date);
  const [start, setStart] = useState(saved.start);
  const [end, setEnd] = useState(saved.end);

  /*
    THE ROW TAKES THE SAVED TIMES ONLY WHEN SHE IS NOT IN IT (2026-10-25). It used to be rebuilt
    whenever a save came back, so changing the date and moving straight on to the time had the
    date's save land mid-edit and wipe what she was typing — the time snapped back and stayed.
  */
  const row = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (row.current?.contains(document.activeElement)) return;
    setDate(saved.date);
    setStart(saved.start);
    setEnd(saved.end);
  }, [saved.date, saved.start, saved.end]);
  const backwards = end !== '' && start !== '' && end <= start;

  const commit = () => {
    if (!date || !start || backwards) return;
    if (date === saved.date && start === saved.start && end === saved.end) return;
    onSave(
      atLocalTimeOn(date, zone, start).toISOString(),
      end ? atLocalTimeOn(date, zone, end).toISOString() : null,
    );
  };

  const n = session.sessionNumber;
  const box = 'h-10 rounded border border-control-border bg-paper px-3 text-[14px] tabular focus-visible:border-deep';
  return (
    <li ref={row} className="flex flex-col gap-1 px-5 py-3.5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-[14px]">
          <span className="tabular mr-3 text-[13px] text-ink-3">{n}</span>
          {WEEKDAYS[weekdayIn(startsAt, zone)]?.slice(0, -1)}
        </span>
        <div className="flex flex-wrap items-center gap-2">
          {/*
            LABELLED, because the week number beside it is not one. Three date boxes in a row are
            three identical "date" controls to a screen reader, and moving the wrong session is a
            change that reaches everybody's calendar. Caught by an axe pass.
          */}
          <input type="date" aria-label={`Session ${n} date`} className={box} value={date} onChange={(e) => setDate(e.target.value)} onBlur={commit} />
          <input type="time" aria-label={`Session ${n} starts`} className={box} value={start} onChange={(e) => setStart(e.target.value)} onBlur={commit} />
          <span className="text-[14px] text-ink-2">to</span>
          <input
            type="time"
            aria-label={`Session ${n} ends`}
            aria-invalid={backwards || undefined}
            className={box}
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            onBlur={commit}
          />
        </div>
      </div>
      {backwards ? (
        <p role="alert" className="text-right text-[12px] text-rust">
          Ends before it starts.
        </p>
      ) : null}
    </li>
  );
}

/**
 * WHERE THE LEAGUE PLAYS, set or changed after setup (owner, 2026-10-12). A league may still say
 * "not decided yet"; a venue it names has a street address — the substitute's email needs it.
 */
function VenueSettings({
  marketId,
  venueId,
  saving,
  onSave,
}: {
  marketId: string;
  venueId: string | null;
  saving: boolean;
  onSave: (patch: Record<string, unknown>) => void;
}) {
  const venues = useQuery({
    queryKey: ['venues', marketId],
    queryFn: () => apiFetch<VenueListResponse>(`/api/markets/${marketId}/venues`),
  });
  const markets = useQuery({
    queryKey: ['markets'],
    queryFn: () => apiFetch<MarketListResponse>('/api/markets'),
  });
  const market = markets.data?.markets.find((m) => m.id === marketId);
  const list = venues.data?.venues ?? [];

  const [choice, setChoice] = useState<VenueChoice>(venueId ? { ...NO_VENUE, venueId } : NO_VENUE);
  const changed = choice.venueId !== (venueId ?? '') || choice.venueId === 'new' || Boolean(choice.address.addressLine1);
  const ready = changed && venueChoiceComplete(choice, list) && !(choice.venueId === 'new' && !choice.name.trim());

  return (
    <Section title="The venue">
      <VenueChooser
        idPrefix="settings"
        venues={list}
        pick
        choice={choice}
        defaultCity={market?.city ?? ''}
        defaultState={market?.state ?? ''}
        onChange={setChoice}
      />
      <div>
        <Button
          size="app"
          disabled={saving || !ready}
          onClick={() => onSave(choice.venueId ? venuePayload(choice, list) : { venueId: null })}
        >
          {saving ? 'Saving…' : 'Save venue'}
        </Button>
      </div>
    </Section>
  );
}
